import React, { useState, useRef, useEffect } from 'react';
import { 
  LayoutDashboard, Users, Wallet, History, ShoppingCart, Palette, 
  FileText, Image, BookOpen, Briefcase, Settings, MessageSquare,
  ListTodo, Camera, ChevronDown, ChevronRight, Plug, Key, Globe,
  DollarSign, CreditCard, TrendingUp, UserCheck, Shield, Database,
  Handshake, Link2, Compass
} from 'lucide-react';
import { cn } from '../../lib/utils';

const menuStructure = [
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    label: { fr: 'Tableau de bord', en: 'Dashboard' },
    color: 'text-blue-600',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30'
  },
  {
    id: 'direction',
    icon: Compass,
    label: { fr: 'Direction', en: 'Direction' },
    color: 'text-amber-600',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    children: [
      { id: 'notes', label: { fr: 'Notes Admin', en: 'Admin Notes' }, icon: MessageSquare },
      { id: 'knowledge', label: { fr: 'Base de connaissances', en: 'Knowledge Base' }, icon: BookOpen },
      { id: 'todo', label: { fr: 'Checklist / To Do', en: 'Checklist / To Do' }, icon: ListTodo },
      { id: 'screenshots', label: { fr: 'Screenshots', en: 'Screenshots' }, icon: Camera }
    ]
  },
  {
    id: 'agencies',
    icon: Handshake,
    label: { fr: 'Agences Locales', en: 'Local Agencies' },
    color: 'text-pink-600',
    bgColor: 'bg-pink-100 dark:bg-pink-900/30',
    children: [
      { id: 'agencies-list', label: { fr: 'Toutes les Agences', en: 'All Agencies' }, icon: Users },
      { id: 'agencies-invitations', label: { fr: 'Invitations', en: 'Invitations' }, icon: UserCheck },
      { id: 'stripe-connect', label: { fr: 'Stripe Connect', en: 'Stripe Connect' }, icon: CreditCard, future: true }
    ]
  },
  {
    id: 'users',
    icon: Users,
    label: { fr: 'Utilisateurs', en: 'Users' },
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
    children: [
      { id: 'users-list', label: { fr: 'Liste des utilisateurs', en: 'User List' }, icon: Users },
      { id: 'users-types', label: { fr: 'Types de comptes', en: 'Account Types' }, icon: UserCheck, future: true },
      { id: 'users-permissions', label: { fr: 'Rôles & Permissions', en: 'Roles & Permissions' }, icon: Shield, future: true }
    ]
  },
  {
    id: 'finances',
    icon: DollarSign,
    label: { fr: 'Finances', en: 'Finances' },
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    children: [
      { id: 'transactions', label: { fr: 'Transactions', en: 'Transactions' }, icon: History },
      { id: 'wallets', label: { fr: 'Portefeuilles', en: 'Wallets' }, icon: Wallet },
      { id: 'withdrawals', label: { fr: 'Retraits en attente', en: 'Pending Withdrawals' }, icon: CreditCard, future: true },
      { id: 'reports', label: { fr: 'Rapports financiers', en: 'Financial Reports' }, icon: TrendingUp, future: true }
    ]
  },
  {
    id: 'woocommerce',
    icon: ShoppingCart,
    label: { fr: 'Woo & Spokes', en: 'Woo & Spokes' },
    color: 'text-purple-600',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    children: [
      { id: 'woo-apikeys', label: { fr: 'Clés API', en: 'API Keys' }, icon: Key },
      { id: 'woo-ipn', label: { fr: 'Configuration IPN', en: 'IPN Configuration' }, icon: Globe },
      { id: 'woo-logs', label: { fr: 'Logs IPN', en: 'IPN Logs' }, icon: FileText },
      { id: 'woo-mycred', label: { fr: 'myCred Sync', en: 'myCred Sync' }, icon: Link2, future: true },
      { id: 'woo-partners', label: { fr: 'Sites partenaires', en: 'Partner Sites' }, icon: Globe, future: true },
      { id: 'woo-plugin', label: { fr: 'Plugin (téléchargement)', en: 'Plugin (download)' }, icon: Plug }
    ]
  },
  {
    id: 'appearance',
    icon: Palette,
    label: { fr: 'Design & CMS', en: 'Design & CMS' },
    color: 'text-orange-600',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    children: [
      { id: 'pages', label: { fr: 'Pages', en: 'Pages' }, icon: FileText },
      { id: 'join-us-editor', label: { fr: 'Page Rejoignez-nous', en: 'Join Us Page' }, icon: Users },
      { id: 'medias', label: { fr: 'Médias', en: 'Media' }, icon: Image },
      { id: 'translations', label: { fr: 'Traductions', en: 'Translations' }, icon: Globe, future: true },
      { id: 'darkmode', label: { fr: 'Mode sombre', en: 'Dark Mode' }, icon: Palette, future: true }
    ]
  },
  {
    id: 'team',
    icon: Briefcase,
    label: { fr: 'Équipe & RH', en: 'Team & HR' },
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
    children: [
      { id: 'positions', label: { fr: 'Positions ouvertes', en: 'Open Positions' }, icon: Briefcase },
      { id: 'applications', label: { fr: 'Candidatures', en: 'Applications' }, icon: Users, future: true },
      { id: 'members', label: { fr: 'Membres de l\'équipe', en: 'Team Members' }, icon: Users, future: true }
    ]
  },
  {
    id: 'settings',
    icon: Settings,
    label: { fr: 'Paramètres', en: 'Settings' },
    color: 'text-gray-600',
    bgColor: 'bg-gray-100 dark:bg-gray-900/30',
    children: [
      { id: 'config', label: { fr: 'Configuration générale', en: 'General Configuration' }, icon: Settings },
      { id: 'integrations', label: { fr: 'Intégrations', en: 'Integrations' }, icon: Plug },
      { id: 'security', label: { fr: 'Sécurité', en: 'Security' }, icon: Shield, future: true },
      { id: 'backups', label: { fr: 'Sauvegardes', en: 'Backups' }, icon: Database, future: true },
      { id: 'sync-tools', label: { fr: 'Outils de Sync', en: 'Sync Tools' }, icon: Database },
      { id: 'logs', label: { fr: 'Logs système', en: 'System Logs' }, icon: FileText, future: true }
    ]
  }
];

export default function AdminSidebar({ activeTab, setActiveTab, language = 'fr', collapsed = false }) {
  const [expandedMenus, setExpandedMenus] = useState(['dashboard']);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const handleItemClick = (item, parentId = null) => {
    if (item.future) return;
    
    if (item.children) {
      // Has children - toggle expand/collapse AND navigate to this tab
      toggleMenu(item.id);
      setActiveTab(item.id); // Also set the active tab to show parent content
    } else {
      // No children - navigate to this tab
      setActiveTab(item.id);
      if (parentId && !expandedMenus.includes(parentId)) {
        setExpandedMenus(prev => [...prev, parentId]);
      }
    }
  };

  const isActive = (itemId) => activeTab === itemId;
  const isParentActive = (item) => {
    if (!item.children) return false;
    return item.children.some(child => 
      child.id === activeTab || 
      (child.children && child.children.some(subChild => subChild.id === activeTab))
    );
  };

  // Animated Submenu Component
  const AnimatedSubmenu = ({ isOpen, children }) => {
    const contentRef = useRef(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
      if (contentRef.current) {
        setHeight(isOpen ? contentRef.current.scrollHeight : 0);
      }
    }, [isOpen, children]);

    return (
      <div 
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ height: `${height}px`, opacity: isOpen ? 1 : 0 }}
      >
        <div ref={contentRef} className="mt-1 space-y-1">
          {children}
        </div>
      </div>
    );
  };

  const renderMenuItem = (item, level = 0, parentId = null) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.id);
    const active = isActive(item.id) || isParentActive(item);
    const isFuture = item.future;

    return (
      <div key={item.id} className="w-full">
        <button
          onClick={() => handleItemClick(item, parentId)}
          disabled={isFuture}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-200",
            level === 0 ? "text-sm font-medium" : "text-sm",
            level === 1 ? "ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700" : "",
            level === 2 ? "ml-8 pl-4 border-l-2 border-gray-200 dark:border-gray-700" : "",
            active && !hasChildren ? "bg-primary/10 text-primary border-l-primary" : "",
            active && hasChildren ? "text-primary" : "",
            !active && !isFuture ? "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800" : "",
            isFuture ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          )}
        >
          {Icon && (
            <div className={cn(
              "flex-shrink-0 h-8 w-8 rounded-lg flex items-center justify-center",
              level === 0 ? item.bgColor : "bg-transparent",
              active ? "ring-2 ring-primary/20" : ""
            )}>
              <Icon className={cn(
                "h-4 w-4",
                level === 0 ? item.color : "text-gray-500",
                active ? "text-primary" : ""
              )} />
            </div>
          )}
          <span className="flex-1 truncate">
            {item.label[language]}
            {isFuture && <span className="ml-2 text-xs text-gray-400">[Bientôt]</span>}
          </span>
          {hasChildren && !item.isSubGroup && (
            <div className="flex-shrink-0 transition-transform duration-300">
              <ChevronDown className={cn(
                "h-4 w-4 text-gray-400 transition-transform duration-300",
                isExpanded ? "rotate-0" : "-rotate-90"
              )} />
            </div>
          )}
        </button>

        {/* Animated Children */}
        {hasChildren && (
          <AnimatedSubmenu isOpen={isExpanded}>
            {item.children.map(child => renderMenuItem(child, level + 1, item.id))}
          </AnimatedSubmenu>
        )}
      </div>
    );
  };

  return (
    <aside className={cn(
      "bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 h-full overflow-y-auto",
      collapsed ? "w-16" : "w-72"
    )}>
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-4 z-10">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#003DA5] to-[#0052CC] flex items-center justify-center">
            <Shield className="h-4 w-4 text-white" />
          </div>
          {!collapsed && (
            <div>
              <h2 className="font-bold text-sm">Admin PayHub</h2>
              <p className="text-xs text-gray-500">Québec+</p>
            </div>
          )}
        </div>
      </div>

      {/* Menu */}
      <nav className="p-3 space-y-1">
        {menuStructure.map(item => renderMenuItem(item))}
      </nav>
    </aside>
  );
}

export { menuStructure };
